// m3-playback-controller.js — station navigation (ARD M3 contract).
// Exposes window.M3 = { init, restoreSelection, resumePlayback, next, previous,
//                       playStation, getCurrentIndex, onNavigate, getPendingPlayback }.
// init() restores selection WITHOUT starting audio (browser autoplay policy);
// resumePlayback() begins audio on the first user gesture. GLM Batch C — T2.2.

window.M3 = (() => {
  // ---------------------------------------------------------------------------
  // Module state
  // ---------------------------------------------------------------------------

  let _currentIndex = null;   // active station index (1–10) or null
  let _navigateCb   = null;   // single-slot onNavigate callback
  let _debounceTimer = null;  // debounce for stream loading
  const STREAM_DEBOUNCE_MS = 200;
  let _pendingPlayback = false; // true until the first user gesture starts audio (T2.2)

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns a sorted array of occupied slot indices from M2.
   * Returns [] if the store is empty or returns unexpected data.
   */
  function _occupiedIndices() {
    try {
      const stations = M2.getStations();
      if (!Array.isArray(stations)) return [];
      return stations
        .filter(s => s && typeof s.index === 'number')
        .map(s => s.index)
        .sort((a, b) => a - b);
    } catch (e) {
      Logger.log({ ts: Date.now(), module: 'M3', fn: '_occupiedIndices', inputs: {}, output: null, durationMs: 0, error: e });
      return [];
    }
  }

  /**
   * Resolves the best available URL for a station given a quality preference.
   * Fallback chain per quality:
   *   Data Saver:  url_lq → url → url_hq
   *   Normal:      url → url_lq → url_hq
   *   High:        url_hq → url → url_lq
   * Empty strings are skipped. Returns the first non-empty URL in the chain.
   */
  function _resolveUrl(station, quality) {
    const chains = {
      'data-saver': ['url_lq', 'url', 'url_hq'],
      'normal':     ['url', 'url_lq', 'url_hq'],
      'high':       ['url_hq', 'url', 'url_lq']
    };
    const order = chains[quality] || chains['normal'];
    for (const key of order) {
      if (station[key] && station[key].trim()) return station[key];
    }
    return station.url || '';
  }

  /**
   * Commands M1 to play the station at the given index.
   * UI updates (onNavigate) fire immediately. Stream loading (M1.play) and
   * metadata fetching (M6.setStation) are debounced 200ms to avoid wasteful
   * loads during fast station browsing.
   * @param {number} index
   * @param {{ skipDebounce?: boolean }} [opts]
   * Returns true on success, { error } if the slot is empty or index is invalid.
   */
  function _playIndex(index, opts) {
    var skipDebounce = opts && opts.skipDebounce;
    const fromIndex = _currentIndex;
    try {
      const stations = M2.getStations();
      if (!Array.isArray(stations)) return { error: 'Station store returned unexpected data' };

      const station = stations.find(s => s && s.index === index);
      if (!station || !station.url) {
        return { error: `Slot ${index} is empty or has no URL` };
      }

      // Resolve the best URL given current quality preference
      const quality = M2.getQualityPreference();
      const resolvedUrl = _resolveUrl(station, quality);

      Logger.log({
        ts: Date.now(), module: 'M3', fn: 'navigation',
        inputs: { from: fromIndex, to: index, quality, resolvedUrl }, output: index, durationMs: 0, error: null,
      });

      // Update current index and fire UI callback immediately —
      // station name and indicator change instantly, without waiting for stream load.
      _currentIndex = index;

      if (_navigateCb) _navigateCb(index);

      // Abort old M6 probe immediately so stale metadata doesn't re-populate
      // the marquee between now and the debounce firing.
      if (typeof M6 !== 'undefined') M6.setStation(index, resolvedUrl);

      // Debounce stream loading: cancel pending, start 200ms timer.
      // Only M1.play() is debounced — M6 is already switched above.
      // Skip debounce for resumePlayback (first playback in a gesture)
      // so the audio element exists immediately for M8 iOS activation.
      if (_debounceTimer) clearTimeout(_debounceTimer);
      if (skipDebounce) {
        _debounceTimer = null;
        M1.play(resolvedUrl);
        M2.setLastPlayedIndex(index);
      } else {
        _debounceTimer = setTimeout(() => {
          _debounceTimer = null;
          M1.play(resolvedUrl);
          M2.setLastPlayedIndex(index);
        }, STREAM_DEBOUNCE_MS);
      }

      return true;
    } catch (e) {
      Logger.log({ ts: Date.now(), module: 'M3', fn: '_playIndex', inputs: { index }, output: null, durationMs: 0, error: e });
      return { error: e.message };
    }
  }

  // ---------------------------------------------------------------------------
  // Public interface (ARD M3 contract)
  // ---------------------------------------------------------------------------

  /**
   * RESTORE SELECTION (T2.2). Sets _currentIndex from M2.getLastPlayedIndex()
   * (falling back to the first occupied slot), fires onNavigate so the UI renders
   * the station name + indicator immediately, seeds M8 via onStationChange, and
   * notifies M6 via setStation so metadata polling begins. Does NOT call M1.play.
   * Sets the pendingPlayback flag so the UI can render the [play] affordance.
   * Returns the restored index, or null if no slots are occupied.
   */
  function _restoreSelection() {
    const occupied = _occupiedIndices();
    if (occupied.length === 0) return null;

    const lastPlayed = M2.getLastPlayedIndex();
    const target     = (lastPlayed && occupied.includes(lastPlayed))
      ? lastPlayed
      : occupied[0];

    // Resolve the station + best URL so we can notify M6 (metadata polling) and
    // M8 (media-session station name) WITHOUT starting audio.
    const stations = M2.getStations();
    const station = stations.find(s => s && s.index === target);
    if (!station || !station.url) return null;

    const quality = M2.getQualityPreference();
    const resolvedUrl = _resolveUrl(station, quality);

    _currentIndex = target;
    _pendingPlayback = true;

    if (_navigateCb) _navigateCb(target);
    if (typeof M6 !== 'undefined') M6.setStation(target, resolvedUrl);
    // Notify M8 for Media Session lock-screen metadata (station name only).
    if (window.M8 && window.M8.onStationChange) window.M8.onStationChange(target);

    Logger.log({
      ts: Date.now(), module: 'M3', fn: 'restoreSelection',
      inputs: { target, quality, resolvedUrl }, output: target, durationMs: 0, error: null,
    });

    return target;
  }

  /**
   * RESUME PLAYBACK (T2.2). The deferred-playback half: clears pendingPlayback
   * and calls _playIndex(_currentIndex) (the existing debounced M1.play path).
   * Idempotent — a no-op if no selection is restored or audio is already going.
   * Invoked once on the first user gesture (registered by main.js).
   */
  function resumePlayback() {
    if (_currentIndex === null) return false;
    _pendingPlayback = false;
    // Skip debounce: this runs inside the first user gesture where iOS
    // Media Session activation needs the audio element to exist immediately.
    return _playIndex(_currentIndex, { skipDebounce: true });
  }

  /**
   * init() — restores the last-played selection WITHOUT starting audio (browser
   * autoplay policy). Equivalent to restoreSelection(). Audio begins on the first
   * user gesture via resumePlayback(). GLM Batch C — T2.2.
   */
  const init = Logger.traced('M3', 'init', () => {
    return _restoreSelection();
  });

  const restoreSelection = Logger.traced('M3', 'restoreSelection', () => {
    return _restoreSelection();
  });

  /**
   * Navigates to the next occupied slot, wrapping from the highest to the lowest.
   * No-op (no error) if no slots are occupied. If pendingPlayback is set (audio
   * not yet started), the first gesture will resume on the navigated-to station.
   */
  const next = Logger.traced('M3', 'next', () => {
    const occupied = _occupiedIndices();
    if (occupied.length === 0) return null;

    if (occupied.length === 1) return _playIndex(occupied[0]);

    const pos  = occupied.indexOf(_currentIndex);
    const nxt  = (pos === -1 || pos === occupied.length - 1)
      ? occupied[0]
      : occupied[pos + 1];

    Logger.log({
      ts: Date.now(), module: 'M3', fn: 'next_resolve',
      inputs: { current: _currentIndex }, output: nxt, durationMs: 0, error: null,
    });

    return _playIndex(nxt);
  });

  /**
   * Navigates to the previous occupied slot, wrapping from the lowest to the highest.
   * No-op (no error) if no slots are occupied.
   */
  const previous = Logger.traced('M3', 'previous', () => {
    const occupied = _occupiedIndices();
    if (occupied.length === 0) return null;

    if (occupied.length === 1) return _playIndex(occupied[0]);

    const pos  = occupied.indexOf(_currentIndex);
    const prev = (pos === -1 || pos === 0)
      ? occupied[occupied.length - 1]
      : occupied[pos - 1];

    Logger.log({
      ts: Date.now(), module: 'M3', fn: 'previous_resolve',
      inputs: { current: _currentIndex }, output: prev, durationMs: 0, error: null,
    });

    return _playIndex(prev);
  });

  /**
   * Directly plays a station by index.
   * Returns { error } if the slot is empty.
   */
  const playStation = Logger.traced('M3', 'playStation', (index) => {
    return _playIndex(index);
  });

  /**
   * Returns the currently active station index (1–10) or null.
   */
  const getCurrentIndex = Logger.traced('M3', 'getCurrentIndex', () => {
    return _currentIndex;
  });

  /**
   * Registers the single-slot navigation callback.
   * Fires after every init(), next(), previous(), and playStation() call.
   * Last registration wins.
   */
  const onNavigate = Logger.traced('M3', 'onNavigate', (callback) => {
    _navigateCb = callback;
    return true;
  });

  const getPendingPlayback = () => _pendingPlayback;

  return {
    init, restoreSelection, resumePlayback, next, previous,
    playStation, getCurrentIndex, onNavigate, getPendingPlayback,
  };
})();
